import { Router } from '@lit-labs/router';
import { LitElement, css, html } from 'lit';
import { sessionStore, signOut } from '../core/auth.js';
import { fallbackRoute, navigate, registerRouter, routes } from '../core/router.js';
import { StoreController } from '../core/store.js';
import { dismissNotification, notify, uiStore } from '../core/ui.js';
import './qz-app-shell.js';
import './qz-invite-watch.js';
import './qz-toast.js';

/**
 * Korzeń aplikacji: spina router, sesję i globalne zdarzenia z powłoką.
 *
 * Sam niczego nie rysuje — wygląd należy do `qz-app-shell`, który dostaje
 * stąd sesję, bieżący adres i wersję, a oddaje zdarzenia logowania
 * i wylogowania. Dzięki temu cała logika zależna od świata (router,
 * store'y, `location`, `window`) siedzi w jednym, cienkim miejscu,
 * a powłokę da się oglądać w izolacji.
 */
export class QzApp extends LitElement {
  #router = new Router(this, routes, { fallback: fallbackRoute });
  #session = new StoreController(this, sessionStore);
  #ui = new StoreController(this, uiStore);

  static styles = css`
    :host {
      display: block;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    registerRouter(this.#router);
    window.addEventListener('qz:session-expired', this.#onSessionExpired);
    window.addEventListener('popstate', this.#rerender);
  }

  disconnectedCallback() {
    window.removeEventListener('qz:session-expired', this.#onSessionExpired);
    window.removeEventListener('popstate', this.#rerender);
    super.disconnectedCallback();
  }

  #rerender = () => this.requestUpdate();

  #onSessionExpired = () => {
    // Nie hałasujemy, gdy użytkownik i tak jest na formularzu logowania.
    if (location.pathname.startsWith('/logowanie')) return;
    if (!sessionStore.get().user) return;

    sessionStore.set({ status: 'guest', user: null });
    notify('Sesja wygasła. Zaloguj się ponownie.', { kind: 'error' });
    navigate(`/logowanie?powrot=${encodeURIComponent(location.pathname)}`);
  };

  async #onSignOut() {
    await signOut();
    notify('Wylogowano.');
    navigate('/');
  }

  render() {
    const { status, user } = this.#session.value;

    // Powiadomienia i zaproszenia idą do slotu `overlay`, żeby odziedziczyły
    // po powłoce zapas na pasek zakładek.
    return html`
      <qz-app-shell
        .user=${user}
        ?loading=${status === 'loading'}
        path=${location.pathname}
        version=${__APP_VERSION__}
        @qz-sign-in=${() => navigate('/logowanie')}
        @qz-sign-out=${this.#onSignOut}
      >
        ${this.#router.outlet()}
        <qz-toast
          slot="overlay"
          .notifications=${this.#ui.value.notifications}
          @qz-dismiss=${(event) => dismissNotification(event.detail.id)}
        ></qz-toast>
        <qz-invite-watch slot="overlay"></qz-invite-watch>
      </qz-app-shell>
    `;
  }
}

customElements.define('qz-app', QzApp);
